/**
 * DatasourceCard Component
 * One connector card: who it is, whether it is live, how fresh it is and what
 * it has cost in queries.
 */

import React from 'react';
import { Datasource } from '../../types/platform.types';
import { StatusBadge } from '../shared/StatusBadge';
import { formatRelativeTime } from '../../utils/formatTime';

interface DatasourceCardProps {
  datasource: Datasource;
  onView: () => void;
  onDelete: () => void;
}

const typeConfig: Record<string, { name: string; color: string; abbr: string }> = {
  salesforce: { name: 'CRM', color: '#00A1E0', abbr: 'SF' },
  sap_erp: { name: 'ERP', color: '#0B66C3', abbr: 'SAP' },
  odoo: { name: 'ERP', color: '#714B67', abbr: 'OD' },
  dynamics365: { name: 'ERP', color: '#0078D4', abbr: 'D3' },
  oracle_erp: { name: 'ERP', color: '#1B3A5C', abbr: 'OR' },
  workday: { name: 'HR', color: '#F5A623', abbr: 'WD' },
  postgresql: { name: 'Database', color: '#31648C', abbr: 'PG' },
  mysql: { name: 'Database', color: '#D9910E', abbr: 'MY' },
  mssql: { name: 'Database', color: '#CE4630', abbr: 'MS' },
  rest_api: { name: 'API', color: '#12B981', abbr: 'API' },
  custom: { name: 'Custom', color: '#69747A', abbr: 'CU' },
};

export const DatasourceCard: React.FC<DatasourceCardProps> = ({
  datasource,
  onView,
  onDelete,
}) => {
  const config =
    typeConfig[datasource.type] || { name: datasource.type, color: '#69747A', abbr: 'DS' };

  return (
    <div className="conn click" onClick={onView}>
      <div className="top2">
        <span className="lg" style={{ background: config.color }}>
          {config.abbr}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="n" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {datasource.name}
          </div>
          <div className="m">
            {config.name}
            {datasource.sync_enabled &&
              (datasource.sync_frequency_minutes
                ? ` · syncs every ${datasource.sync_frequency_minutes}m`
                : ' · auto-sync on')}
          </div>
        </div>
        <StatusBadge status={datasource.status as any} size="sm" />
      </div>

      {datasource.description && (
        <div
          style={{
            fontSize: 11.5,
            color: 'var(--muted)',
            marginTop: 9,
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {datasource.description}
        </div>
      )}

      {datasource.last_sync_error && datasource.status === 'error' && (
        <div className="errbox" style={{ marginTop: 9 }}>
          {datasource.last_sync_error}
        </div>
      )}

      <div className="stats">
        <div>
          <span>Fresh</span>
          <b>{datasource.last_sync_at ? formatRelativeTime(datasource.last_sync_at) : '—'}</b>
        </div>
        <div>
          <span>Queries</span>
          <b>{datasource.total_queries !== undefined ? datasource.total_queries : '—'}</b>
        </div>
        <div>
          <span>Avg</span>
          <b>
            {datasource.avg_response_time_ms !== undefined
              ? `${datasource.avg_response_time_ms}ms`
              : '—'}
          </b>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 7, marginTop: 11 }}>
        <button
          className="btn btn-g btn-s"
          style={{ flex: 1 }}
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
        >
          View details
        </button>
        <button
          className="btn btn-r btn-s"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
};
